import { yupResolver } from '@hookform/resolvers/yup';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { DialogActions, DialogContent, DialogTitle, Grid, IconButton } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { getDefaultRevenueDetails, saveDefaultRevenueDetails } from 'wisestep-api';
import {
  CardVariant,
  SmtpSetupConfigIcon,
  WsButton,
  WsCard,
  WsColors,
  WsDialog,
  WsListingLoader,
  WsTypoVariants,
  WsTypography,
} from 'wisestep-base';
import { number, object } from 'yup';

import { SettingsPageLayout } from '../../../components/SettingsPageLayout';
import { useWorkspaceContext } from '../../../context/WorkspaceContext';
import {
  getSettingBreadCrumbList,
  ISettingBreadCrumbTypes,
  Pages,
} from '../../../features/teams/helper/defaultValues';
import { success, failed } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { placementSettingsIndexOptions } from '../helper/ClosureHelper';

import ContractRevenueCalculatorForm from './ContractRevenueCalculatorForm';

export interface ContractFormType {
  standardHours: number;
  standardDays: number;
  standardMonths: number;
  overrideStandardMore: boolean;
  overrideStandardLess: boolean;
  workspaceId: string;
}
const ContractRevenueCalculator = () => {
  const workspace = useWorkspaceContext();
  const defaultValues: ContractFormType = {
    standardHours: 8,
    standardDays: 18,
    standardMonths: 4,
    overrideStandardMore: false,
    overrideStandardLess: true,
    workspaceId: workspace.id || '',
  };
  const validationSchema = {
    standardHours: number()
      .nullable()
      .transform((value) => (isNaN(value) ? undefined : value))
      .required('Standard Hours is required')
      .min(0, "Standard Hours can't be less than 0")
      .max(24, "standard Hours can't be more than 24"),
    standardDays: number()
      .nullable()
      .transform((value) => (isNaN(value) ? undefined : value))
      .required('Standard Days is required')
      .min(0, "Standard Days can't be less than 0")
      .max(31, "standard Days can't be more than 31"),
    standardMonths: number()
      .nullable()
      .transform((value) => (isNaN(value) ? undefined : value))
      .required('Standard Months is required')
      .min(0, "Standard Months can't be less than 0")
      .max(12, "standard Months can't be more than 12"),
  };
  const [data, setData] = useState<ContractFormType | null>(null);
  const [loader, setLoader] = useState<boolean>(false);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const setNotification = useNotificationStore((state) => state.setNotification);
  const methods = useForm<ContractFormType>({
    resolver: yupResolver(object().shape(validationSchema)),
    defaultValues: defaultValues,
    mode: 'onChange',
  });
  const { handleSubmit } = methods;
  const onSubmit = async (state: ContractFormType) => {
    setOpenDialog(false);
    setLoader(true);
    try {
      const response = await saveDefaultRevenueDetails(state);
      setNotification(success('Data sucessessfully saved!'));
      setData(response.data);
    } catch (error) {
      console.log(error);
      setNotification(failed('Error occured while saving the data!'));
    } finally {
      setLoader(false);
    }
  };
  const fetchData = useCallback(async () => {
    setLoader(true);
    try {
      const response = await getDefaultRevenueDetails();
      setData(response.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoader(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, []);
  useEffect(() => {
    if (data?.standardDays) {
      methods.reset(data);
    }
  }, [data]);
  return (
    <SettingsPageLayout
      title="Contract Revenue Calculator"
      indexOptions={placementSettingsIndexOptions}
      breadCrumnbsList={getSettingBreadCrumbList(
        ISettingBreadCrumbTypes.PLACEMENTS,
        Pages['CONTRACT_REVENUE_CALCULATOR']
      )}
      hideCard={data?.standardDays ? false : true}
    >
      <FormProvider {...methods}>
        {loader ? (
          <WsListingLoader />
        ) : data?.standardDays ? (
          <Grid mt={4}>
            <ContractRevenueCalculatorForm methods={methods} editable={false} />
          </Grid>
        ) : (
          <Grid>
            <WsCard
              cardType={CardVariant.WITH_OUT_BG}
              title="Setup your standard Revenue Calculator"
              description="You can only setup the values once"
              middleComponent={<SmtpSetupConfigIcon />}
              isButton={true}
              buttonText="Setup"
              btnStartIcon={<AddOutlinedIcon />}
              onClick={() => setOpenDialog(true)}
            />

            <WsDialog openDialog={openDialog} onClose={() => setOpenDialog(false)} size={'medium'}>
              <DialogTitle>
                <Grid container direction="row" justifyContent="space-between">
                  <Grid item>
                    <WsTypography variant={WsTypoVariants.TITLE} bold={true}>
                      Contract Revenue Calculator
                    </WsTypography>
                  </Grid>
                  <Grid item>
                    <IconButton onClick={() => setOpenDialog(false)}>
                      <CloseIcon sx={{ fill: WsColors.GreyBlack, cursor: 'pointer' }} />
                    </IconButton>
                  </Grid>
                </Grid>
              </DialogTitle>
              <DialogContent sx={{ p: 0 }}>
                <ContractRevenueCalculatorForm methods={methods} />
              </DialogContent>
              <DialogActions sx={{ p: 1 }}>
                <WsButton onClick={handleSubmit(onSubmit)}>Save</WsButton>
              </DialogActions>
            </WsDialog>
          </Grid>
        )}
      </FormProvider>
    </SettingsPageLayout>
  );
};
export default ContractRevenueCalculator;
